import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import {
  executeDelete,
  executeGet,
  executePatch,
  executePost,
} from '@/services/lukita'
import type { CaseFact, CaseFactDTO } from './types'

export interface CaseFactsStore {
  facts: CaseFact[]
  actions: {
    reset: () => void
    setCaseFacts: (facts: CaseFactDTO[]) => void
  }
  loaders: {
    fetchCaseFacts: (slug: string) => Promise<void>
  }
  mutations: {
    addCaseFact: (slug: string, body: string) => Promise<void>
    updateCaseFact: (slug: string, id: number, body: string) => Promise<void>
    deleteCaseFact: (slug: string, id: number) => Promise<void>
  }
}

// Writes refetch the list instead of patching it, so a broadcast that lands first cannot add a duplicate.
export const useCaseFactsStore = create<CaseFactsStore>((set, get, store) => {
  const refetch = (slug: string) => get().loaders.fetchCaseFacts(slug)

  return {
    facts: [],
    actions: {
      reset: () => set(store.getInitialState()),
      setCaseFacts: (facts) =>
        set({ facts: camelcaseKeys(facts) as CaseFact[] }),
    },
    loaders: {
      fetchCaseFacts: async (slug) => {
        const response = await executeGet(`/projects/${slug}/case_facts`)
        get().actions.setCaseFacts(await response.json())
      },
    },
    mutations: {
      addCaseFact: async (slug, body) => {
        await executePost(`/projects/${slug}/case_facts`, {
          case_fact: { body },
        })
        await refetch(slug)
      },
      updateCaseFact: async (slug, id, body) => {
        await executePatch(`/projects/${slug}/case_facts/${id}`, {
          case_fact: { body },
        })
        await refetch(slug)
      },
      deleteCaseFact: async (slug, id) => {
        await executeDelete(`/projects/${slug}/case_facts/${id}`)
        await refetch(slug)
      },
    },
  }
})
